import type {
  ChatCall,
  ChatCallTrackName,
  ChatIceServer,
  ChatSessionDescription,
  ChatSfuPublishRequest,
  ChatSfuPublishResponse,
  ChatSfuSessionResponse,
  ChatSfuSubscribeRequest,
  ChatSfuSubscribeResponse,
  ChatSfuUnpublishRequest,
  ChatSfuUnsubscribeRequest,
} from '@bits/shared/zod';
import type { RemoteTrackLike, RtpSenderLike } from './webrtc';

// Calls on the SFU: one peer connection to Cloudflare's edge, our own tracks
// published on it, everyone else's pulled onto it. The server tells us what
// exists through the call's participant list; this class reconciles against
// that list and keeps every mutation on one queue, because the SFU requires
// the operations on a session (and the SDP exchange that completes each) to
// happen one at a time.

export interface SfuApi {
  session(): Promise<ChatSfuSessionResponse>;
  publish(body: ChatSfuPublishRequest): Promise<ChatSfuPublishResponse>;
  subscribe(body: ChatSfuSubscribeRequest): Promise<ChatSfuSubscribeResponse>;
  renegotiate(body: { sessionDescription: ChatSessionDescription }): Promise<void>;
  unpublish(body: ChatSfuUnpublishRequest): Promise<void>;
  unsubscribe(body: ChatSfuUnsubscribeRequest): Promise<void>;
}

export interface TransceiverLike {
  readonly mid: string | null;
  readonly sender: RtpSenderLike;
  readonly receiver: { readonly track: MediaStreamTrack & RemoteTrackLike };
  stop(): void;
}

export interface SfuPeerConnectionLike {
  addTransceiver(
    trackOrKind: MediaStreamTrack | 'audio' | 'video',
    init?: { direction: string },
  ): TransceiverLike;
  getTransceivers(): TransceiverLike[];
  createOffer(): Promise<RTCSessionDescriptionInit>;
  createAnswer(): Promise<RTCSessionDescriptionInit>;
  setLocalDescription(description: RTCSessionDescriptionInit): Promise<void>;
  setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void>;
  close(): void;
  readonly localDescription: RTCSessionDescriptionInit | null;
  readonly connectionState: string;
  ontrack:
    | ((event: { track: MediaStreamTrack & RemoteTrackLike; transceiver: TransceiverLike }) => void)
    | null;
  onconnectionstatechange: (() => void) | null;
}

export interface SfuOptions {
  meId: string;
  localStream: MediaStream | null;
  api: SfuApi;
  onRemoteStream: (peerId: string, stream: MediaStream | null) => void;
  onRemoteVideo?: (peerId: string, active: boolean) => void;
  onPeerScreen?: (peerId: string, sharing: boolean) => void;
  onPeerState?: (peerId: string, state: string) => void;
  onError?: (err: unknown) => void;
  createConnection?: () => SfuPeerConnectionLike;
  createStream?: (tracks: MediaStreamTrack[]) => MediaStream;
  iceServers?: ChatIceServer[];
}

interface Pulled {
  peerId: string;
  name: ChatCallTrackName;
  mid: string;
  track: (MediaStreamTrack & RemoteTrackLike) | null;
}

interface Peer {
  sessionId: string;
  tracks: Map<ChatCallTrackName, MediaStreamTrack & RemoteTrackLike>;
}

export interface WantedTrack {
  key: string;
  peerId: string;
  sessionId: string;
  name: ChatCallTrackName;
}

const trackKey = (sessionId: string, name: string): string => `${sessionId}/${name}`;

/** Every (session, track) pair of other joined participants: what this browser should be pulling. */
export function wantedTracks(call: ChatCall, meId: string): WantedTrack[] {
  const out: WantedTrack[] = [];
  for (const p of call.participants) {
    if (p.personId === meId || p.status !== 'joined' || p.session === null) continue;
    for (const t of p.session.tracks) {
      out.push({
        key: trackKey(p.session.sessionId, t.name),
        peerId: p.personId,
        sessionId: p.session.sessionId,
        name: t.name,
      });
    }
  }
  return out;
}

function sessionDescription(d: RTCSessionDescriptionInit): ChatSessionDescription {
  if ((d.type !== 'offer' && d.type !== 'answer') || !d.sdp) {
    throw new Error('Incomplete session description');
  }
  return { type: d.type, sdp: d.sdp };
}

export class SfuCall {
  private pc: SfuPeerConnectionLike;
  private queue: Promise<unknown> = Promise.resolve();
  private closed = false;
  private started = false;
  private published = new Map<ChatCallTrackName, TransceiverLike>();
  private pulled = new Map<string, Pulled>();
  private byMid = new Map<string, string>();
  private peers = new Map<string, Peer>();
  private createStream: (tracks: MediaStreamTrack[]) => MediaStream;

  constructor(private opts: SfuOptions) {
    this.createStream = opts.createStream ?? ((tracks) => new MediaStream(tracks));
    this.pc = (opts.createConnection ??
      (() =>
        new RTCPeerConnection({
          bundlePolicy: 'max-bundle',
          iceServers: (opts.iceServers ?? []).map((s) => ({
            urls: s.urls,
            ...(s.username !== undefined ? { username: s.username } : {}),
            ...(s.credential !== undefined ? { credential: s.credential } : {}),
          })),
        }) as unknown as SfuPeerConnectionLike))();
    this.pc.ontrack = (event) => {
      const mid = event.transceiver.mid;
      const key = mid === null ? undefined : this.byMid.get(mid);
      const entry = key === undefined ? undefined : this.pulled.get(key);
      if (entry) this.attach(entry, event.track);
    };
    this.pc.onconnectionstatechange = () => {
      for (const peerId of this.peers.keys()) this.opts.onPeerState?.(peerId, this.pc.connectionState);
    };
  }

  isSharingScreen(): boolean {
    return this.published.has('screen');
  }

  publishedTracks(): ChatCallTrackName[] {
    return [...this.published.keys()];
  }

  pulledKeys(): string[] {
    return [...this.pulled.keys()];
  }

  /** Create the session and publish the microphone. The camera and the screen are published on request. */
  start(): Promise<void> {
    return this.run(async () => {
      if (this.started) return;
      await this.opts.api.session();
      this.started = true;
      const mic = this.opts.localStream?.getAudioTracks()[0];
      if (mic) await this.publish('mic', mic);
    });
  }

  setCameraTrack(track: MediaStreamTrack | null): Promise<void> {
    return this.run(() => this.setPublished('cam', track));
  }

  setScreenTrack(track: MediaStreamTrack | null): Promise<void> {
    return this.run(() => this.setPublished('screen', track));
  }

  /** Reconcile what is pulled against the call's current participants. */
  sync(call: ChatCall): Promise<void> {
    return this.run(async () => {
      if (!this.started) return;
      const wanted = wantedTracks(call, this.opts.meId);
      const wantedKeys = new Set(wanted.map((w) => w.key));
      const stale = [...this.pulled.keys()].filter((key) => !wantedKeys.has(key));
      if (stale.length > 0) await this.drop(stale);
      const fresh = wanted.filter((w) => !this.pulled.has(w.key));
      if (fresh.length > 0) await this.pull(fresh);
      for (const peerId of [...this.peers.keys()]) {
        if (!wanted.some((w) => w.peerId === peerId)) this.forget(peerId);
      }
    });
  }

  close(): void {
    this.closed = true;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    this.pc.close();
    for (const peerId of [...this.peers.keys()]) this.forget(peerId);
    this.published.clear();
    this.pulled.clear();
    this.byMid.clear();
  }

  private run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    const next = this.queue.then(async () => {
      if (this.closed) return undefined;
      try {
        return await fn();
      } catch (err) {
        this.opts.onError?.(err);
        return undefined;
      }
    });
    this.queue = next;
    return next;
  }

  private async setPublished(name: 'cam' | 'screen', track: MediaStreamTrack | null): Promise<void> {
    if (!this.started) return;
    const current = this.published.get(name);
    if (track === null) {
      if (!current) return;
      this.published.delete(name);
      current.stop();
      await this.opts.api.unpublish({ tracks: [{ name }] });
      return;
    }
    if (current) {
      await current.sender.replaceTrack(track);
      return;
    }
    await this.publish(name, track);
  }

  // The browser offers, the SFU answers. Mids exist only after
  // setLocalDescription, so they are read from the transceiver afterwards.
  private async publish(name: ChatCallTrackName, track: MediaStreamTrack): Promise<void> {
    const transceiver = this.pc.addTransceiver(track, { direction: 'sendonly' });
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    const mid = transceiver.mid;
    if (mid === null) throw new Error('No mid after setLocalDescription');
    const kind = name === 'mic' ? 'audio' : 'video';
    const res = await this.opts.api.publish({
      sessionDescription: sessionDescription(this.pc.localDescription ?? offer),
      tracks: [{ name, kind, mid }],
    });
    await this.pc.setRemoteDescription(res.sessionDescription);
    this.published.set(name, transceiver);
  }

  // The SFU offers, the browser answers through renegotiate. The mids come
  // back in the subscribe response, so `ontrack` can resolve them to a peer
  // while the offer is being applied.
  private async pull(fresh: WantedTrack[]): Promise<void> {
    const res = await this.opts.api.subscribe({
      tracks: fresh.map((w) => ({ sessionId: w.sessionId, name: w.name })),
    });
    for (const t of res.tracks) {
      const key = trackKey(t.sessionId, t.name);
      const wanted = fresh.find((w) => w.key === key);
      if (!wanted) continue;
      this.pulled.set(key, { peerId: wanted.peerId, name: t.name, mid: t.mid, track: null });
      this.byMid.set(t.mid, key);
      const peer = this.peers.get(wanted.peerId);
      if (!peer) {
        this.peers.set(wanted.peerId, { sessionId: wanted.sessionId, tracks: new Map() });
        this.opts.onPeerState?.(wanted.peerId, this.pc.connectionState);
      } else peer.sessionId = wanted.sessionId;
    }
    if (res.sessionDescription) {
      await this.pc.setRemoteDescription(res.sessionDescription);
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      await this.opts.api.renegotiate({
        sessionDescription: sessionDescription(this.pc.localDescription ?? answer),
      });
    }
    for (const transceiver of this.pc.getTransceivers()) {
      const key = transceiver.mid === null ? undefined : this.byMid.get(transceiver.mid);
      const entry = key === undefined ? undefined : this.pulled.get(key);
      if (entry && entry.track === null) this.attach(entry, transceiver.receiver.track);
    }
  }

  private async drop(keys: string[]): Promise<void> {
    const mids: string[] = [];
    for (const key of keys) {
      const entry = this.pulled.get(key);
      if (!entry) continue;
      this.pulled.delete(key);
      this.byMid.delete(entry.mid);
      mids.push(entry.mid);
      const transceiver = this.pc.getTransceivers().find((t) => t.mid === entry.mid);
      transceiver?.stop();
      const peer = this.peers.get(entry.peerId);
      if (peer && peer.tracks.get(entry.name) === entry.track) {
        peer.tracks.delete(entry.name);
        this.emit(entry.peerId);
      }
    }
    if (mids.length > 0) await this.opts.api.unsubscribe({ tracks: mids.map((mid) => ({ mid })) });
  }

  private attach(entry: Pulled, track: MediaStreamTrack & RemoteTrackLike): void {
    entry.track = track;
    const peer = this.peers.get(entry.peerId);
    if (!peer) return;
    peer.tracks.set(entry.name, track);
    if (track.kind === 'video') {
      track.onunmute = () => this.emit(entry.peerId);
      track.onmute = () => this.emit(entry.peerId);
    }
    this.emit(entry.peerId);
  }

  // One stream per person: their microphone plus whichever video matters
  // most, the screen over the camera. The tile code is the same as the mesh's.
  private emit(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    const mic = peer.tracks.get('mic');
    const screen = peer.tracks.get('screen');
    const video = screen ?? peer.tracks.get('cam') ?? null;
    const tracks = [mic, video].filter(
      (t): t is MediaStreamTrack & RemoteTrackLike => t !== undefined && t !== null,
    );
    this.opts.onRemoteStream(peerId, this.createStream(tracks));
    this.opts.onPeerScreen?.(peerId, screen !== undefined);
    this.opts.onRemoteVideo?.(peerId, video !== null && !video.muted);
  }

  private forget(peerId: string): void {
    if (!this.peers.delete(peerId)) return;
    this.opts.onRemoteStream(peerId, null);
    this.opts.onRemoteVideo?.(peerId, false);
    this.opts.onPeerScreen?.(peerId, false);
  }
}
